import { createClient } from '@supabase/supabase-js'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string

// Helper constants for session cache maintenance
const SUPABASE_PROJECT_REF = (() => {
  try {
    const url = new URL(supabaseUrl)
    return url.hostname.split('.')[0]
  } catch {
    return null
  }
})()

// Funkcja pomocnicza do pobierania klucza storage
// Używamy funkcji zamiast stałej, aby uniknąć problemów z cache przeglądarki
export const getAuthStorageKey = (): string => {
  if (typeof window === 'undefined') {
    return 'supabase.auth.token'
  }
  
  /**
   * WAŻNE: storageKey musi być deterministyczny.
   *
   * Wcześniej braliśmy "pierwszy lepszy" klucz `sb-*-auth-token` z localStorage.
   * To jest niebezpieczne, bo użytkownik może mieć w przeglądarce kilka takich kluczy
   * (np. stare środowiska / inne projekty / subdomeny) i wtedy aplikacja czytała
   * nie ten token → po refreshu wyglądało to jak wylogowanie.
   */
  if (SUPABASE_PROJECT_REF) {
    return `sb-${SUPABASE_PROJECT_REF}-auth-token`
  }

  // Fallback: jeśli nie umiemy wyliczyć ref-a (nietypowy URL), spróbuj znaleźć istniejący klucz.
  try {
    const existingKeys = Object.keys(window.localStorage).filter(key =>
      key.startsWith('sb-') && key.endsWith('-auth-token')
    )
    if (existingKeys.length === 1) {
      return existingKeys[0]
    }
    if (existingKeys.length > 1) {
      // Nie zgadujemy – to powodowało "losowe" gubienie sesji.
      console.warn(
        '[supabase] Wykryto wiele kluczy sb-*-auth-token w localStorage; używam fallback supabase.auth.token',
        existingKeys
      )
    }
  } catch {
    // ignore
  }

  return 'supabase.auth.token'
}

// Eksportujemy również stałą dla kompatybilności wstecznej
export const AUTH_STORAGE_KEY = getAuthStorageKey()

const clearKey = (key: string) => {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.removeItem(key)
  } catch (error) {
    console.error('Nie udało się usunąć klucza z localStorage', { key, error })
  }
}

export const clearSupabaseSessionCache = () => {
  clearKey(AUTH_STORAGE_KEY)
  clearKey(`${AUTH_STORAGE_KEY}-user`)
  clearKey(`${AUTH_STORAGE_KEY}-code-verifier`)
}

/**
 * WYŁĄCZONE: Walidacja sesji PRZED utworzeniem klienta
 * 
 * PROBLEM: Walidacja była zbyt agresywna i czyściła sesję zanim Supabase zdążył odświeżyć token
 * ROZWIĄZANIE: Całkowicie wyłączamy walidację - pozwalamy Supabase samemu zarządzać sesją
 * 
 * Supabase automatycznie:
 * - Odświeża tokeny gdy wygasną (używając refresh_token)
 * - Czyści uszkodzone sesje
 * - Zarządza wygasłymi tokenami
 * 
 * Nasze funkcje walidacyjne tylko przeszkadzały w tym procesie.
 */
// const sanitizeSupabaseSessionCache = () => {
//   // WYŁĄCZONE - pozwalamy Supabase samemu zarządzać sesją
// }

// WYŁĄCZONE: Walidacja PRZED utworzeniem klienta
// if (typeof window !== 'undefined') {
//   sanitizeSupabaseSessionCache()
// }

// Sprawdzenie czy konfiguracja istnieje
if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error(
    '❌ Brakuje konfiguracji Supabase!\n\n' +
    'Lokalne środowisko: Utwórz plik .env z:\n' +
    '  VITE_SUPABASE_URL=twoj-url\n' +
    '  VITE_SUPABASE_ANON_KEY=twoj-klucz\n\n' +
    'Produkcja (Netlify): Dodaj zmienne w Site Settings → Environment Variables'
  )
}

// Custom storage adapter używający TYLKO localStorage (bez cookies)
// Zapobiega problemom z cookies które mogą powodować problemy z przeglądaniem
const localStorageAdapter = {
  getItem: (key: string): string | null => {
    if (typeof window === 'undefined') return null
    try {
      return window.localStorage.getItem(key)
    } catch (error) {
      console.error('Błąd odczytu z localStorage:', error)
      return null
    }
  },
  setItem: (key: string, value: string): void => {
    if (typeof window === 'undefined') return
    try {
      window.localStorage.setItem(key, value)
    } catch (error) {
      console.error('Błąd zapisu do localStorage:', error)
    }
  },
  removeItem: (key: string): void => {
    if (typeof window === 'undefined') return
    try {
      window.localStorage.removeItem(key)
    } catch (error) {
      console.error('Błąd usuwania z localStorage:', error)
    }
  },
}

// Tworzenie klienta Supabase z konfiguracją auth
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    // Wyłącz automatyczne odświeżanie przy focus okna
    // (zapobiega czyszczeniu formularzy przy przełączaniu zakładek)
    autoRefreshToken: true, // Pozostaw włączone dla bezpieczeństwa
    persistSession: true,
    detectSessionInUrl: true,
    // WAŻNE: Nie odświeżaj sesji przy każdym focus okna
    flowType: 'pkce', // Używaj PKCE dla lepszego bezpieczeństwa
    // WAŻNE: Wymuszenie użycia localStorage zamiast cookies
    // Zapobiega problemom z cookies które mogą powodować problemy z przeglądaniem strony
    storage: localStorageAdapter,
    storageKey: AUTH_STORAGE_KEY,
  },
  global: {
    headers: {
      'X-Client-Info': 'wojskaspecjalne-web',
    },
  },
})

/**
 * WYŁĄCZONE: Walidacja sesji PO utworzeniu klienta Supabase
 * 
 * PROBLEM: Walidacja była zbyt agresywna i czyściła sesję zanim Supabase zdążył odświeżyć token
 * ROZWIĄZANIE: Całkowicie wyłączamy walidację - pozwalamy Supabase samemu zarządzać sesją
 * 
 * Supabase automatycznie:
 * - Odświeża tokeny gdy wygasną (używając refresh_token)
 * - Czyści uszkodzone sesje
 * - Zarządza wygasłymi tokenami
 * - Emituje eventy TOKEN_REFRESHED gdy token zostanie odświeżony
 * 
 * Nasze funkcje walidacyjne tylko przeszkadzały w tym procesie.
 * AuthContext już obsługuje eventy Supabase (TOKEN_REFRESHED, SIGNED_OUT, etc.)
 */
// const validateSupabaseSession = async () => {
//   // WYŁĄCZONE - pozwalamy Supabase samemu zarządzać sesją
// }

// WYŁĄCZONE: Walidacja PO utworzeniu klienta
// if (typeof window !== 'undefined') {
//   // WYŁĄCZONE - pozwalamy Supabase samemu zarządzać sesją
// }

// Helper function to check if using local Supabase
export const isLocalSupabase = (): boolean => {
  return supabaseUrl?.includes('localhost') || supabaseUrl?.includes('127.0.0.1')
}

